import type { PlayerMfaListResult, PlayerMfaSummary, PlayerMfaUser } from './types'

/** Demo / offline fixture — shapes match Player-API admin MFA responses. */
export const DEMO_PLAYER_MFA_SUMMARY: PlayerMfaSummary = {
  totalUsers: 8,
  mfaEnabled: 5,
  mfaDisabled: 3,
  totpEnabled: 4,
  smsEnabled: 2,
  bothEnabled: 1,
  withRecoveryCodes: 4,
  adoptionRate: 5 / 8,
}

export const DEMO_PLAYER_MFA_USERS: PlayerMfaUser[] = [
  {
    id: 'demo-p1',
    username: 'member1',
    email: 'member1@strix.local',
    displayName: 'Member One',
    userType: 'player',
    mfaEnabled: true,
    methods: ['totp', 'sms'],
    factors: [
      { type: 'totp', enabled: true, phoneMasked: null, updatedAt: '2026-07-01T10:00:00.000Z' },
      {
        type: 'sms',
        enabled: true,
        phoneMasked: '+91 ******3210',
        updatedAt: '2026-07-02T10:00:00.000Z',
      },
    ],
    hasRecoveryCodes: true,
    createdAt: '2026-01-10T08:00:00.000Z',
    updatedAt: '2026-07-02T10:00:00.000Z',
  },
  {
    id: 'demo-p2',
    username: 'riya.k',
    email: 'riya.k@example.com',
    displayName: 'Riya K',
    userType: 'player',
    mfaEnabled: true,
    methods: ['totp'],
    factors: [
      { type: 'totp', enabled: true, phoneMasked: null, updatedAt: '2026-06-15T12:00:00.000Z' },
    ],
    hasRecoveryCodes: true,
    createdAt: '2026-02-01T08:00:00.000Z',
    updatedAt: '2026-06-15T12:00:00.000Z',
  },
  {
    id: 'demo-p3',
    username: 'arjun.s',
    email: 'arjun.s@example.com',
    displayName: 'Arjun S',
    userType: 'player',
    mfaEnabled: true,
    methods: ['sms'],
    factors: [
      {
        type: 'sms',
        enabled: true,
        phoneMasked: '+91 ******8899',
        updatedAt: '2026-05-20T09:00:00.000Z',
      },
    ],
    hasRecoveryCodes: false,
    createdAt: '2026-03-12T08:00:00.000Z',
    updatedAt: '2026-05-20T09:00:00.000Z',
  },
  {
    id: 'demo-p4',
    username: 'guest.demo',
    email: 'guest@strix.local',
    displayName: 'Guest Demo',
    userType: 'player',
    mfaEnabled: false,
    methods: [],
    factors: [],
    hasRecoveryCodes: false,
    createdAt: '2026-04-01T08:00:00.000Z',
    updatedAt: '2026-04-01T08:00:00.000Z',
  },
  {
    id: 'demo-p5',
    username: 'neha.m',
    email: 'neha.m@example.com',
    displayName: 'Neha M',
    userType: 'player',
    mfaEnabled: true,
    methods: ['totp'],
    factors: [
      { type: 'totp', enabled: true, phoneMasked: null, updatedAt: '2026-07-10T14:00:00.000Z' },
    ],
    hasRecoveryCodes: true,
    createdAt: '2026-01-20T08:00:00.000Z',
    updatedAt: '2026-07-10T14:00:00.000Z',
  },
  {
    id: 'demo-p6',
    username: 'vikram.p',
    email: 'vikram.p@example.com',
    displayName: 'Vikram P',
    userType: 'player',
    mfaEnabled: false,
    methods: [],
    factors: [],
    hasRecoveryCodes: false,
    createdAt: '2026-05-05T08:00:00.000Z',
    updatedAt: '2026-05-05T08:00:00.000Z',
  },
  {
    id: 'demo-p7',
    username: 'sara.l',
    email: 'sara.l@example.com',
    displayName: 'Sara L',
    userType: 'player',
    mfaEnabled: true,
    methods: ['totp'],
    factors: [
      { type: 'totp', enabled: true, phoneMasked: null, updatedAt: '2026-06-01T11:00:00.000Z' },
    ],
    hasRecoveryCodes: true,
    createdAt: '2026-02-18T08:00:00.000Z',
    updatedAt: '2026-06-01T11:00:00.000Z',
  },
  {
    id: 'demo-p8',
    username: 'open.bet',
    email: 'open.bet@example.com',
    displayName: 'Open Bet',
    userType: 'player',
    mfaEnabled: false,
    methods: [],
    factors: [],
    hasRecoveryCodes: false,
    createdAt: '2026-06-22T08:00:00.000Z',
    updatedAt: '2026-06-22T08:00:00.000Z',
  },
]

export function filterDemoUsers(query: {
  q?: string
  mfa?: 'all' | 'on' | 'off'
  method?: 'any' | 'totp' | 'sms'
  limit?: number
  offset?: number
}): PlayerMfaListResult {
  const q = (query.q || '').trim().toLowerCase()
  const mfa = query.mfa || 'all'
  const method = query.method || 'any'
  let rows = DEMO_PLAYER_MFA_USERS.slice()
  if (q) {
    rows = rows.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.displayName.toLowerCase().includes(q),
    )
  }
  if (mfa === 'on') rows = rows.filter((u) => u.mfaEnabled)
  if (mfa === 'off') rows = rows.filter((u) => !u.mfaEnabled)
  if (method === 'totp') rows = rows.filter((u) => u.methods.includes('totp'))
  if (method === 'sms') rows = rows.filter((u) => u.methods.includes('sms'))
  const limit = Math.min(200, Math.max(1, query.limit ?? 50))
  const offset = Math.max(0, query.offset ?? 0)
  return {
    users: rows.slice(offset, offset + limit),
    total: rows.length,
    limit,
    offset,
  }
}

export function findDemoUser(idOrUsername: string): PlayerMfaUser | null {
  const key = idOrUsername.trim().toLowerCase()
  return (
    DEMO_PLAYER_MFA_USERS.find(
      (u) => u.id.toLowerCase() === key || u.username.toLowerCase() === key,
    ) ?? null
  )
}
